import { gsap } from "gsap";
import { Signal } from "typed-signals";
import { LayoutController, SoundVolumeController } from "onearm";

/**
 * @class SoundPopupController
 * @description Controller for sound popup with volume slider and mute toggle
 */
export class SoundPopupController extends LayoutController {
    init() {
        this.onVolumeChange = new Signal();
        this.onMutedChange = new Signal();

        this._muted = this.options.isSoundsMuted;
        this._volume = this.options.volume;

        this.popup = this.get("SoundVolumeControlls");
        this.parent = this.options.parent;

        this.setupSoundVolumeController();
        this.setupButtons();
        this.updateButtons();
        this.hidePopup(true);

        this.onHidePopupHandler = this.handleHidePopup.bind(this);
    }

    setupSoundVolumeController() {
        if (!this.popup) return;

        this.soundVolumeController = new SoundVolumeController(this.popup, {
            volume: this._volume,
            muted: this._muted
        });

        this.connectSignal(this.soundVolumeController.onVolumeChange, (vol) => {
            this._volume = vol;
            this.onVolumeChange.emit(vol);
            this.updateButtons();
        });

        this.connectSignal(this.soundVolumeController.onMutedChange, (muted) => {
            this._muted = muted;
            this.onMutedChange.emit(muted);
            this.updateButtons();
        });
    }

    setupButtons() {
        this.forAll("SoundOnButton", (btn) => {
            btn.onPress.connect(this.handleButtonPress.bind(this));
        });

        this.forAll("SoundOffButton", (btn) => {
            btn.onPress.connect(this.handleButtonPress.bind(this));
        });
    }

    handleButtonPress() {
        if (!this.popup?.visible) {
            this.showPopup();
            this.toggleMute();
        } else {
            this.hidePopup();
        }
    }

    toggleMute() {
        this._muted = !this._muted;
        this.soundVolumeController?.updateMutedState(this._muted);
        this.onMutedChange.emit(this._muted);
        this.updateButtons();
    }

    updateButtons() {
        this.setVisible("SoundOffButton", this._muted);
        this.setVisible("SoundOnButton", !this._muted);
    }

    handleHidePopup(e) {
        const soundOnButton = this.get("SoundOnButton");
        const soundOffButton = this.get("SoundOffButton");

        if (!this.hitTarget(e, soundOnButton) &&
            !this.hitTarget(e, soundOffButton) &&
            !this.hitTarget(e, this.popup)) {
            this.hidePopup(true);
        }
    }

    hitTarget(e, target) {
        if (!target) return false;
        const targetBounds = target.getBounds();
        return (
            e.global.x > targetBounds.x &&
            e.global.x < targetBounds.x + targetBounds.width &&
            e.global.y > targetBounds.y &&
            e.global.y < targetBounds.y + targetBounds.height
        );
    }

    showPopup() {
        if (!this.popup) return;

        this.popup.visible = true;
        gsap.killTweensOf(this.popup);

        const tl = gsap.timeline();
        tl.playSfx("common_popup_windows");
        tl.to(this.popup, {
            alpha: 1,
            duration: 0.25,
            ease: "power2.inOut",
            onComplete: () => {
                if (this.parent) {
                    this.parent.interactive = true;
                    this.parent.interactiveChildren = true;
                    this.parent.on("pointerdown", this.onHidePopupHandler);
                }
            },
        });
    }

    hidePopup(silent = false) {
        if (!this.popup) return;

        gsap.killTweensOf(this.popup);

        if (this.parent) {
            this.parent.off("pointerdown", this.onHidePopupHandler);
        }

        const tl = gsap.timeline();
        if (!silent) {
            tl.playSfx("hide_popup");
        }
        tl.to(this.popup, {
            alpha: 0,
            duration: 0.25,
            ease: "power2.inOut",
            onComplete: () => {
                this.popup.visible = false;
            },
        });
    }

    get muted() {
        return this._muted;
    }

    get volume() {
        return this._volume;
    }

    onDestroy() {
        if (this.parent) {
            this.parent.off("pointerdown", this.onHidePopupHandler);
        }
    }
}

